import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  DbOntologyProcedure,
  useCreateDbOntologyProcedure,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyProcedureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  procedure?: DbOntologyProcedure | null;
  onSaved?: () => void;
  // Viewer-role opens the same dialog to inspect details, but every input
  // is disabled and the save button is hidden. Row-click in the parent
  // relies on this to give viewers a read path for existing rows.
  isReadOnly?: boolean;
}

interface FormState {
  name: string;
  schema: string;
  procedureType: string;
  parameters: string;
  returnType: string;
  body: string;
  comment: string;
}

const EMPTY: FormState = {
  name: "",
  schema: "",
  procedureType: "",
  parameters: "",
  returnType: "",
  body: "",
  comment: "",
};

// Parse parameters: accept JSON array (of strings OR objects), or comma /
// newline separated names. Objects pass through as-is so the backend
// receives a proper array-of-objects instead of an array of embedded JSON
// strings — matches CreateDDLProcedureDto.parameters (`any[]`).
const parseParameters = (raw: string): any[] | undefined => {
  const text = raw.trim();
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // fall through to split
  }
  return text
    .split(/[\n,]/g)
    .map((p) => p.trim())
    .filter(Boolean);
};

// Backend rejects `name` updates on plain stored procedures (see
// db-ontology-graph.service.ts:updateProcedure). Lock UI + skip `name` only
// when the original type is exactly `procedure`; every other type (function,
// trigger, or anything else) is passed through unchanged.
const isPlainProcedureType = (procedureType?: string): boolean =>
  (procedureType ?? "").trim().toLowerCase() === "procedure";

export default function DbOntologyProcedureDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  procedure,
  onSaved,
  isReadOnly = false,
}: DbOntologyProcedureDialogProps) {
  const isEdit = !!procedure?.id;
  // Anchor to the procedure's original type so switching type in the form
  // can't bypass the backend rule. Read-only mode always locks inputs.
  const nameLocked =
    isReadOnly || (isEdit && isPlainProcedureType(procedure?.procedureType));
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createProcedure = useCreateDbOntologyProcedure();
  const updateNode = useUpdateDbOntologyNode();

  useEffect(() => {
    if (!isOpen) return;
    if (procedure) {
      setForm({
        name: procedure.name || "",
        schema: procedure.schema || "",
        procedureType: procedure.procedureType || "",
        parameters: procedure.parameters || "",
        returnType: procedure.returnType || "",
        body: procedure.body || "",
        comment: procedure.description || "",
      });
    } else {
      setForm({ ...EMPTY });
    }
  }, [isOpen, procedure]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.procedureType.trim()) return;
    const params = parseParameters(form.parameters);
    try {
      if (isEdit && procedure?.id) {
        await updateNode.mutateAsync({
          label: "DDLProcedure",
          projectUuid,
          id: procedure.id,
          dataLakeId,
          data: {
            // Plain `procedure` rows aren't renameable — omit `name` from
            // the payload so the backend accepts the update. `procedureType`
            // is still sent (disabled in the UI, but the backend needs it to
            // evaluate its rename rules and keep the type consistent).
            ...(nameLocked ? {} : { name: form.name.trim() }),
            ...(form.schema.trim() ? { schema: form.schema.trim() } : {}),
            ...(form.procedureType.trim()
              ? { procedureType: form.procedureType.trim() }
              : {}),
            // Backend UpdateDDLNodeDto.parameters is @IsArray() and
            // stringifies the value server-side (see
            // db-ontology-graph.service.ts:updateProcedure). Send the array
            // as-is — sending JSON.stringify(...) here fails validation.
            ...(params ? { parameters: params } : {}),
            ...(form.returnType.trim() ? { returnType: form.returnType.trim() } : {}),
            ...(form.body.trim() ? { body: form.body.trim() } : {}),
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
          },
        });
      } else {
        await createProcedure.mutateAsync({
          projectUuid,
          dataLakeId,
          name: form.name.trim(),
          schema: form.schema.trim() || undefined,
          procedureType: form.procedureType.trim() || undefined,
          parameters: params,
          returnType: form.returnType.trim() || undefined,
          body: form.body.trim() || undefined,
          comment: form.comment.trim() || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const isPending = createProcedure.isPending || updateNode.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly
              ? "View Procedure"
              : isEdit
                ? "Edit Procedure"
                : "Create Procedure"}
            {dataLakeLabel ? ` — ${dataLakeLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the procedure fields for the selected data lake.
          </DialogDescription>
        </DialogHeader>

        {/* pl-1 balances pr-1 so the focus ring on the leftmost input edge
            isn't clipped by the parent's `overflow-auto` (CUSTOM_SCROLLBAR). */}
        <div className={`space-y-4 overflow-y-auto flex-1 pl-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Enter procedure name"
              disabled={nameLocked}
              readOnly={isReadOnly}
              // px-4 (16px) instead of the base px-3 so the leftmost char
              // isn't visually clipped by the focus ring on long values
              // like `WATCH_CONTACT_PHONE_NUMBER123`.
              className="bg-surface text-white px-4 disabled:opacity-60 disabled:cursor-not-allowed"
            />
            {/* {nameLocked && (
              <p className="text-xs text-muted">
                Stored procedures can't be renamed.
              </p>
            )} */}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Schema</label>
              <Input
                value={form.schema}
                onChange={(e) => update("schema", e.target.value)}
                placeholder="e.g. public"
                readOnly={isReadOnly}
                // Match Name's px-4 so the leftmost char has breathing room
                // when focused; keeps padding consistent across all inputs.
                className="bg-surface text-white px-4"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">
                Procedure Type <span className="text-error">*</span>
              </label>
              <Input
                value={form.procedureType}
                onChange={(e) => update("procedureType", e.target.value)}
                placeholder="e.g. function"
                disabled={nameLocked}
                readOnly={isReadOnly}
                className="bg-surface text-white px-4 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Return Type</label>
            <Input
              value={form.returnType}
              onChange={(e) => update("returnType", e.target.value)}
              placeholder="e.g. NUMBER"
              readOnly={isReadOnly}
              className="bg-surface text-white px-4"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Parameters</label>
            <Textarea
              value={form.parameters}
              onChange={(e) => update("parameters", e.target.value)}
              placeholder='Comma/newline separated, or JSON array (e.g. ["p_id", "p_name"])'
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[60px] font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Comment</label>
            <Textarea
              value={form.comment}
              onChange={(e) => update("comment", e.target.value)}
              placeholder="Optional description for this procedure"
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[60px]"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Body (SQL)</label>
            <Textarea
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              placeholder="BEGIN ... END;"
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[120px] font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={!form.name.trim() || !form.procedureType.trim() || isPending}
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
